import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getCuponsDiscussaoAdminControllerListarQueryKey,
  useCuponsDiscussaoAdminControllerAtualizar,
  useCuponsDiscussaoAdminControllerCriar,
} from '@/api/generated/admin-cupons-de-desconto-discussão/admin-cupons-de-desconto-discussão';
import type { CupomDiscussaoResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

const schema = z
  .object({
    codigo: z.string().trim().min(3, 'Informe um código com ao menos 3 caracteres').max(60),
    descricao: z.string().trim().max(500),
    percentualDesconto: z.string().trim().min(1, 'Informe o percentual de desconto'),
    /** Texto, não número: em branco vira `null` (sem teto), não `0`. */
    usosMaximos: z.string().trim(),
    permiteReusoPorUsuario: z.boolean(),
    validoDe: z.string(),
    validoAte: z.string(),
  })
  .superRefine((valores, ctx) => {
    const percentual = Number(valores.percentualDesconto);
    if (!Number.isInteger(percentual) || percentual < 1 || percentual > 100) {
      ctx.addIssue({
        code: 'custom',
        path: ['percentualDesconto'],
        message: 'Informe um número inteiro de 1 a 100 — 100 abre a discussão sem cobrança nenhuma.',
      });
    }

    if (valores.usosMaximos) {
      const usos = Number(valores.usosMaximos);
      if (!Number.isInteger(usos) || usos < 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['usosMaximos'],
          message: 'Informe um número inteiro de 1 em diante, ou deixe em branco para sem limite.',
        });
      }
    }

    if (valores.validoDe && valores.validoAte && valores.validoDe > valores.validoAte) {
      ctx.addIssue({
        code: 'custom',
        path: ['validoAte'],
        message: 'O fim da vigência não pode ser antes do início.',
      });
    }
  });

type Formulario = z.infer<typeof schema>;

/** `<input type="date">` fala "2026-07-29"; a API fala ISO completo. */
function paraData(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

function paraIso(data: string, fimDoDia = false): string | null {
  if (!data) return null;
  return new Date(`${data}T${fimDoDia ? '23:59:59' : '00:00:00'}.000Z`).toISOString();
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. A listagem já traz tudo — sem busca de detalhe extra. */
  cupom?: CupomDiscussaoResponse;
}

/**
 * Cupom de desconto no pagamento da discussão técnica
 * (docs/cupons-desconto-discussao.md do backend) — modelo e permissões
 * (`admin_cupons_discussao.*`) inteiramente separados do cupom do Clube
 * (`ClubeCuponsTab`); só a tela é compartilhada, via aba em `CuponsPage`.
 * `ativo` não entra aqui: é alternado direto na listagem
 * (`DiscussaoCuponsTab`), mesmo padrão do Clube.
 */
export function CupomDiscussaoFormDialogo({ aberto, aoFechar, cupom }: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(cupom);

  const criar = useCuponsDiscussaoAdminControllerCriar();
  const atualizar = useCuponsDiscussaoAdminControllerAtualizar();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!aberto) return;
    reset({
      codigo: cupom?.codigo ?? '',
      descricao: cupom?.descricao ?? '',
      percentualDesconto: cupom?.percentualDesconto.toString() ?? '',
      usosMaximos: cupom?.usosMaximos?.toString() ?? '',
      permiteReusoPorUsuario: cupom?.permiteReusoPorUsuario ?? false,
      validoDe: paraData(cupom?.validoDe ?? null),
      validoAte: paraData(cupom?.validoAte ?? null),
    });
  }, [aberto, cupom, reset]);

  const aoEnviar = handleSubmit(async (bruto) => {
    const dados = schema.parse(bruto);

    const corpo = {
      codigo: dados.codigo,
      descricao: dados.descricao.trim() || null,
      percentualDesconto: Number(dados.percentualDesconto),
      usosMaximos: dados.usosMaximos ? Number(dados.usosMaximos) : null,
      permiteReusoPorUsuario: dados.permiteReusoPorUsuario,
      validoDe: paraIso(dados.validoDe),
      validoAte: paraIso(dados.validoAte, true),
    };

    try {
      if (cupom) {
        await atualizar.mutateAsync({ id: cupom.id, data: corpo });
      } else {
        await criar.mutateAsync({ data: corpo });
      }

      await queryClient.invalidateQueries({
        queryKey: getCuponsDiscussaoAdminControllerListarQueryKey(),
      });
      toast.success(editando ? 'Cupom atualizado.' : 'Cupom criado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o cupom.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar cupom' : 'Novo cupom de desconto'}</DialogTitle>
          <DialogDescription>
            O médico digita este código na etapa de pagamento ao abrir uma discussão técnica.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo
            id="codigo"
            rotulo="Código"
            erro={errors.codigo?.message}
            obrigatorio
            dica="O médico digita exatamente isto. Maiúsculas e minúsculas não importam."
          >
            <Input id="codigo" className={CLASSE_CONTROLE} placeholder="BEMVINDO50" {...register('codigo')} />
          </Campo>

          <Campo
            id="percentualDesconto"
            rotulo="Desconto (%)"
            erro={errors.percentualDesconto?.message}
            obrigatorio
            dica="100 abre a discussão na hora, sem cobrança nenhuma."
          >
            <Input
              id="percentualDesconto"
              type="number"
              min={1}
              max={100}
              className={CLASSE_CONTROLE}
              {...register('percentualDesconto')}
            />
          </Campo>

          <div className="grid grid-cols-2 gap-3">
            <Campo
              id="usosMaximos"
              rotulo="Limite de usos"
              erro={errors.usosMaximos?.message}
              dica="Em branco = sem limite."
            >
              <Input
                id="usosMaximos"
                type="number"
                min={1}
                placeholder="Sem limite"
                className={CLASSE_CONTROLE}
                {...register('usosMaximos')}
              />
            </Campo>

            <div className="space-y-2">
              <Label htmlFor="permiteReusoPorUsuario" className="text-brand font-medium block">
                Repete por médico?
              </Label>
              <div className="flex items-center h-9">
                <Controller
                  control={control}
                  name="permiteReusoPorUsuario"
                  render={({ field }) => (
                    <Switch
                      id="permiteReusoPorUsuario"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            Desligado (padrão): cada médico usa este código no máximo 1 vez, para sempre.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Campo id="validoDe" rotulo="Válido de" erro={errors.validoDe?.message}>
              <Input id="validoDe" type="date" className={CLASSE_CONTROLE} {...register('validoDe')} />
            </Campo>

            <Campo
              id="validoAte"
              rotulo="Válido até"
              erro={errors.validoAte?.message}
              dica="Em branco = sem prazo."
            >
              <Input id="validoAte" type="date" className={CLASSE_CONTROLE} {...register('validoAte')} />
            </Campo>
          </div>

          <Campo id="descricao" rotulo="Descrição (uso interno)" erro={errors.descricao?.message}>
            <Textarea id="descricao" rows={2} className={CLASSE_CONTROLE} {...register('descricao')} />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar cupom'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
